import type { ReactElement, ReactNode } from 'react'

/**
 * The top of every page: where am I, what is this, what can I do here.
 *
 * One `<h1>` per page lives here and nowhere else. Before the design
 * system each screen rolled its own `text-2xl font-semibold` heading and
 * several rendered two `<h1>`s, which is a real navigation failure for
 * anyone jumping by heading.
 *
 * The title is sized down from the usual dashboard 24px: this is a dense
 * console and the heading is orientation, not a banner. Hierarchy comes
 * from weight and the muted description, not from size.
 *
 * BELOW the `<h1>` there are exactly two heading looks in this app, and
 * there is no primitive for either — they are two class strings, written
 * out at each site, and the rule is which one a heading gets:
 *
 * - **Panel title** — the heading a card or side panel leads with, e.g.
 *   "Session detail", "New item", "Skill versions":
 *   `text-sm font-semibold text-fg`.
 * - **Section micro-label** — a quiet label above a group of rows, a
 *   table or a tree, e.g. "Values", "Branch rules", "Recent events",
 *   "Nodes": `text-xs font-medium tracking-wide text-muted uppercase`.
 *
 * The parallel design-system migration produced a third look
 * (`text-xs font-medium text-muted`, no tracking, no case change) across
 * the dashboard, Composio and sessions groups while the vault group used
 * the micro-label, so two pages showing the same kind of thing did not
 * look related. `text-xs font-medium text-muted` on its own is now the
 * FORM LABEL (`Field`), and nothing else uses it.
 *
 * Page titles are sentence case — "Memory documents", "Composio
 * integrations" — not Title Case.
 */

export interface PageHeaderProps {
  title: string
  description?: ReactNode
  /** The breadcrumb trail, above the title. A `<nav>` is expected here —
   * the slot does not wrap it, because a breadcrumb needs its own label. */
  breadcrumb?: ReactNode
  /** Page-level controls, right-aligned and vertically centred on the
   * title. Wraps below on a narrow viewport rather than squashing it. */
  actions?: ReactNode
}

export function PageHeader({
  title,
  description,
  breadcrumb,
  actions,
}: PageHeaderProps): ReactElement {
  return (
    <header className="mb-4 border-b border-subtle pb-3">
      {breadcrumb !== undefined && <div className="mb-1.5">{breadcrumb}</div>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold text-fg">{title}</h1>
          {description !== undefined && (
            <p className="mt-0.5 text-xs text-muted">{description}</p>
          )}
        </div>
        {actions !== undefined && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>
    </header>
  )
}
